import { useState, useEffect } from 'react';
import api, { aiWorkforceAPI, campaignsAPI } from '../api';
import ReactMarkdown from 'react-markdown';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Bot, Users, Brain, Cpu, Play, Pause, Settings, Plus, Search,
  CheckCircle, XCircle, Clock, AlertTriangle, Activity, Zap,
  FileText, Shield, Target, TrendingUp, Eye, ChevronRight,
  Sparkles, RefreshCw, BarChart3, List, Upload, Book, Lightbulb,
  Image as ImageIcon, Video, Mic, PieChart, Trash2, Edit, ChevronDown, FolderPlus,
  BookOpen, Scale, Info, ExternalLink, Copy, Maximize2, Save, Mail, ListTodo,
  FolderOpen, GitBranch, Kanban, LayoutGrid, Download, Link, FileSpreadsheet, FileType, UserPlus,
  History, RotateCcw, Shuffle, Check, ThumbsUp, ThumbsDown, MessageSquare, Workflow, X
} from 'lucide-react';

// Import constants and components from extracted files
import { 
  STATUS_COLORS, 
  TASK_STATUS_COLORS, 
  EditOvernightTaskDialog, 
  AddOvernightTaskDialog, 
  CreateCustomProcessDialog,
  SaveQuickRunDialog,
  LogDetailDialog,
  UploadResourceDialog,
  ViewResourceDialog,
  KnowledgeDialog,
  GuidelineDialog,
  SOPDialog,
  AgentDetailDialog,
  EditAgentDialog,
  QueueTaskDetailDialog,
  CreateCustomAgentDialog,
  DeliverableDetailDialog,
  CampaignLinkDialog,
  RegenerateDialog,
  ImportPreviewDialog,
  VariationsDialog,
  HistoryDialog,
  ImportFromAssetsDialog,
  TemplateDialog,
  ProjectDialog,
  TaskDetailDialog,
  DashboardTab,
  AgentsTab,
  QueueTab,
  SOPsTab,
  KnowledgeTab,
  LogsTab,
  ProjectsTab,
  GalleryTab,
  ResourcesTab,
  DemoModeTab,
  OvernightQueueTab,
  AllAITasksTab,
  DEFAULT_OVERNIGHT_TASK_FORM,
  DEFAULT_PROCESS_FORM,
  DEFAULT_KNOWLEDGE_FORM,
  DEFAULT_GUIDELINE_FORM,
  DEFAULT_SOP_FORM,
  MODEL_OPTIONS
} from './ai-workforce';

export default function AIWorkforcePage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Agents
  const [agents, setAgents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [savingAgent, setSavingAgent] = useState(false);
  
  // Work Queue
  const [queueTasks, setQueueTasks] = useState([]);
  const [queueFilter, setQueueFilter] = useState({ status: '', agent_id: '' });
  const [selectedQueueTask, setSelectedQueueTask] = useState(null);  // NEW: For queue detail dialog
  const [showQueueDetailDialog, setShowQueueDetailDialog] = useState(false);
  
  // Custom Agent Creation
  const [showCreateAgentDialog, setShowCreateAgentDialog] = useState(false);
  const [newAgentForm, setNewAgentForm] = useState({
    name: '',
    role_title: '',
    description: '',
    agent_type: 'text',  // text, image, video, voice, data
    primary_model: 'gpt-4o',
    capabilities: [],
    knowledge_base_ids: [],
    guideline_ids: []
  });
  
  // SOPs
  const [sops, setSOPs] = useState([]);
  
  // Knowledge Base
  const [knowledge, setKnowledge] = useState([]);
  const [showKnowledgeDialog, setShowKnowledgeDialog] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [knowledgeForm, setKnowledgeForm] = useState(DEFAULT_KNOWLEDGE_FORM);
  
  // Guidelines
  const [guidelines, setGuidelines] = useState([]);
  const [showGuidelineDialog, setShowGuidelineDialog] = useState(false);
  const [editingGuideline, setEditingGuideline] = useState(null);
  const [guidelineForm, setGuidelineForm] = useState(DEFAULT_GUIDELINE_FORM);
  
  // SOPs
  const [showSOPDialog, setShowSOPDialog] = useState(false);
  const [editingSOP, setEditingSOP] = useState(null);
  const [sopForm, setSOPForm] = useState(DEFAULT_SOP_FORM);
  
  // Execution Logs
  const [executionLogs, setExecutionLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDetailDialog, setShowLogDetailDialog] = useState(false);
  
  // Demo Mode
  const [demoTaskTypes, setDemoTaskTypes] = useState([]);
  const [selectedDemoTask, setSelectedDemoTask] = useState(null);
  const [selectedDemoAgent, setSelectedDemoAgent] = useState('');
  const [demoCustomInput, setDemoCustomInput] = useState('');
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [streamingMode, setStreamingMode] = useState(true);
  const [streamingPhase, setStreamingPhase] = useState(null);
  const [demoCompleted, setDemoCompleted] = useState(false);  // Track if demo completed (keeps sections expanded)
  const [leftBrainStream, setLeftBrainStream] = useState('');
  const [rightBrainStream, setRightBrainStream] = useState('');
  const [brainStemStream, setBrainStemStream] = useState('');
  const [streamingAgent, setStreamingAgent] = useState(null);
  const [expandedBrain, setExpandedBrain] = useState(null);
  const [expandedResultBrain, setExpandedResultBrain] = useState({ left: false, right: false, cortex: true });
  
  // Dashboard collapsible sections
  const [collapsedSections, setCollapsedSections] = useState({
    agentPerformance: false,
    recentTasks: false,
    failedTasks: false
  });
  const [dashboardFilter, setDashboardFilter] = useState(null); // 'active_agents', 'in_progress', 'completed_today', 'awaiting_approval', 'failed'
  const [failedTasks, setFailedTasks] = useState([]);
  
  // Auto-Task Creation & Deliverables
  const [createdTasks, setCreatedTasks] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [executingTasks, setExecutingTasks] = useState(false);
  
  // Quick Runs
  const [quickRuns, setQuickRuns] = useState([]);
  const [showSaveQuickRunDialog, setShowSaveQuickRunDialog] = useState(false);
  const [quickRunName, setQuickRunName] = useState('');
  
  // Deliverables Gallery
  const [galleryDeliverables, setGalleryDeliverables] = useState([]);
  const [galleryProjects, setGalleryProjects] = useState([]);
  const [galleryStats, setGalleryStats] = useState(null);
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [gallerySearch, setGallerySearch] = useState('');
  const [galleryViewMode, setGalleryViewMode] = useState('projects'); // 'projects' or 'flat'
  const [expandedProjects, setExpandedProjects] = useState({});
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);
  const [showDeliverableDialog, setShowDeliverableDialog] = useState(false);
  const [editingDeliverable, setEditingDeliverable] = useState(false);
  const [deliverableEditForm, setDeliverableEditForm] = useState({ title: '', description: '', type: '' });
  
  // AI Projects
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [projectKanban, setProjectKanban] = useState(null);
  
  // Resource Library
  const [resources, setResources] = useState([]);
  const [resourceStats, setResourceStats] = useState(null);
  const [resourceFilter, setResourceFilter] = useState('all');
  const [resourceViewMode, setResourceViewMode] = useState('ai_library'); // 'ai_library', 'asset_library', 'combined'
  const [linkedAssets, setLinkedAssets] = useState([]);
  const [combinedResources, setCombinedResources] = useState([]);
  const [showUploadResourceDialog, setShowUploadResourceDialog] = useState(false);
  const [showImportFromAssetsDialog, setShowImportFromAssetsDialog] = useState(false);
  const [newResource, setNewResource] = useState({ name: '', description: '', category: 'product', tags: '', file: null, preview: null });
  const [selectedResource, setSelectedResource] = useState(null);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  
  // Campaign Linking
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignLinkDialog, setShowCampaignLinkDialog] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  
  // Import Preview
  const [showImportPreviewDialog, setShowImportPreviewDialog] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});
  const [importLoading, setImportLoading] = useState(false);
  
  // Export Loading
  const [exportLoading, setExportLoading] = useState(false);
  
  // Variations & Revisions
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regenerateFeedback, setRegenerateFeedback] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [variations, setVariations] = useState([]);
  const [showVariationsDialog, setShowVariationsDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [deliverableHistory, setDeliverableHistory] = useState(null);
  const [selectedVariationIndex, setSelectedVariationIndex] = useState(null);
  
  // Task Detail Dialog (for Kanban)
  const [selectedKanbanTask, setSelectedKanbanTask] = useState(null);
  const [showTaskDetailDialog, setShowTaskDetailDialog] = useState(false);
  const [taskDetail, setTaskDetail] = useState(null);
  const [loadingTaskDetail, setLoadingTaskDetail] = useState(false);
  
  // Mind Map View
  const [projectViewMode, setProjectViewMode] = useState('kanban'); // 'kanban' or 'mindmap'
  
  // Event Triggers
  const [eventTriggers, setEventTriggers] = useState([]);
  const [eventTypes, setEventTypes] = useState({});
  
  // Overnight Queue
  const [overnightQueue, setOvernightQueue] = useState({ tasks: [], stats: {}, config: null });
  const [overnightQueueLogs, setOvernightQueueLogs] = useState([]);
  const [showAddOvernightTaskDialog, setShowAddOvernightTaskDialog] = useState(false);
  const [editingOvernightTask, setEditingOvernightTask] = useState(null);
  const [showEditOvernightTaskDialog, setShowEditOvernightTaskDialog] = useState(false);
  const [overnightTaskForm, setOvernightTaskForm] = useState(DEFAULT_OVERNIGHT_TASK_FORM);
  const [overnightQueueRunning, setOvernightQueueRunning] = useState(false);
  
  // Custom Processes
  const [customProcesses, setCustomProcesses] = useState([]);
  const [showCreateProcessDialog, setShowCreateProcessDialog] = useState(false);
  const [newProcessForm, setNewProcessForm] = useState(DEFAULT_PROCESS_FORM);
  // Note: newProcessTaskForm is now managed inside CreateCustomProcessDialog component

  // All AI Tasks (Global View)
  const [allAITasks, setAllAITasks] = useState([]);
  const [allAITasksLoading, setAllAITasksLoading] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchAgents();
  }, []);

  useEffect(() => {
    if (activeTab === 'queue') {
      fetchQueue();
    } else if (activeTab === 'sops') {
      fetchSOPs();
    } else if (activeTab === 'knowledge') {
      fetchKnowledge();
      fetchGuidelines();
    } else if (activeTab === 'logs') {
      fetchExecutionLogs();
    } else if (activeTab === 'demo') {
      fetchDemoTaskTypes();
      fetchQuickRuns();
      fetchCampaignsForDemo();
    } else if (activeTab === 'gallery') {
      fetchGalleryDeliverables();
      fetchGalleryStats();
    } else if (activeTab === 'projects') {
      fetchProjects();
    } else if (activeTab === 'resources') {
      fetchResources();
    } else if (activeTab === 'overnight') {
      fetchOvernightQueue();
      fetchOvernightQueueLogs();
      fetchCustomProcesses();
    } else if (activeTab === 'all-ai-tasks') {
      fetchAllAITasks();
    }
  }, [activeTab]);

  const fetchQuickRuns = async () => {
    try {
      const res = await aiWorkforceAPI.getQuickRuns();
      setQuickRuns(res.data);
    } catch (error) {
      console.error('Failed to fetch quick runs:', error);
    }
  };

  const fetchCampaignsForDemo = async () => {
    try {
      const res = await campaignsAPI.getAll({ limit: 100 });
      setCampaigns(res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchGalleryDeliverables = async () => {
    try {
      const params = { group_by_project: true };
      if (galleryFilter !== 'all') {
        params.deliverable_type = galleryFilter;
      }
      if (gallerySearch) {
        params.search = gallerySearch;
      }
      const res = await aiWorkforceAPI.getDeliverables(params);
      setGalleryDeliverables(res.data.deliverables || []);
      setGalleryProjects(res.data.projects || []);
    } catch (error) {
      console.error('Failed to fetch deliverables:', error);
    }
  };

  const fetchGalleryStats = async () => {
    try {
      const res = await aiWorkforceAPI.getDeliverablesStats();
      setGalleryStats(res.data);
    } catch (error) {
      console.error('Failed to fetch deliverables stats:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await aiWorkforceAPI.getProjects({});
      setProjects(res.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchProjectKanban = async (projectId) => {
    try {
      const res = await aiWorkforceAPI.getProjectKanban(projectId);
      setProjectKanban(res.data);
    } catch (error) {
      console.error('Failed to fetch project kanban:', error);
    }
  };

  const fetchResources = async () => {
    try {
      const params = {};
      if (resourceFilter !== 'all') {
        params.category = resourceFilter;
      }
      
      if (resourceViewMode === 'ai_library') {
        const res = await aiWorkforceAPI.getResources(params);
        setResources(res.data.resources || []);
        setResourceStats(res.data);
      } else if (resourceViewMode === 'asset_library') {
        const res = await aiWorkforceAPI.getLinkedAssets(params);
        setLinkedAssets(res.data.assets || []);
        setResourceStats({ total: res.data.total, by_category: res.data.by_type });
      } else {
        // Combined view
        params.source = 'all';
        const res = await aiWorkforceAPI.getCombinedResources(params);
        setCombinedResources(res.data.resources || []);
        setResourceStats({
          total: res.data.stats?.combined_total || 0,
          ai_library_count: res.data.stats?.ai_library_count || 0,
          asset_library_count: res.data.stats?.asset_library_count || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    }
  };

  const handleSyncToAssetLibrary = async (resourceId) => {
    try {
      await aiWorkforceAPI.syncResourceToAssetLibrary(resourceId, null);
      toast.success('Resource synced to Asset Library!');
      fetchResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync resource');
    }
  };

  const handleImportFromAssetLibrary = async (assetId, category = 'product') => {
    try {
      await aiWorkforceAPI.importAssetToResources(assetId, category);
      toast.success('Asset imported to Resource Library!');
      fetchResources();
      setShowImportFromAssetsDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import asset');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    try {
      await aiWorkforceAPI.deleteResource(resourceId);
      toast.success('Resource deleted');
      setShowResourceDialog(false);
      fetchResources();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  // Overnight Queue Functions
  const fetchOvernightQueue = async () => {
    try {
      const res = await aiWorkforceAPI.getOvernightQueue();
      setOvernightQueue(res.data);
      setOvernightQueueRunning(res.data.config?.is_running || false);
    } catch (error) {
      console.error('Failed to fetch overnight queue:', error);
    }
  };

  const fetchOvernightQueueLogs = async () => {
    try {
      const res = await aiWorkforceAPI.getOvernightQueueLogs();
      setOvernightQueueLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch overnight queue logs:', error);
    }
  };

  const handleAddOvernightTask = async () => {
    if (!overnightTaskForm.title || !overnightTaskForm.description) {
      toast.error('Please fill in title and description');
      return;
    }
    
    try {
      await aiWorkforceAPI.addToOvernightQueue(overnightTaskForm);
      toast.success('Task added to overnight queue');
      setShowAddOvernightTaskDialog(false);
      setOvernightTaskForm(DEFAULT_OVERNIGHT_TASK_FORM);
      fetchOvernightQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add task');
    }
  };

  const handleRemoveOvernightTask = async (taskId) => {
    try {
      await aiWorkforceAPI.removeFromOvernightQueue(taskId);
      toast.success('Task removed from queue');
      fetchOvernightQueue();
    } catch (error) {
      toast.error('Failed to remove task');
    }
  };

  const handleRunOvernightQueueNow = async () => {
    try {
      setOvernightQueueRunning(true);
      await aiWorkforceAPI.runOvernightQueueNow();
      toast.success('Overnight queue processing started');
      
      // Poll for updates
      const pollInterval = setInterval(async () => {
        const res = await aiWorkforceAPI.getOvernightQueue();
        setOvernightQueue(res.data);
        if (!res.data.config?.is_running) {
          setOvernightQueueRunning(false);
          clearInterval(pollInterval);
          fetchOvernightQueueLogs();
          toast.success('Overnight queue processing complete');
        }
      }, 3000);
      
    } catch (error) {
      setOvernightQueueRunning(false);
      toast.error(error.response?.data?.detail || 'Failed to start overnight queue');
    }
  };

  const handleClearCompletedOvernightTasks = async () => {
    try {
      await aiWorkforceAPI.clearCompletedOvernightTasks();
      toast.success('Completed tasks cleared');
      fetchOvernightQueue();
    } catch (error) {
      toast.error('Failed to clear tasks');
    }
  };

  // Custom Processes Functions
  const fetchCustomProcesses = async () => {
    try {
      const res = await aiWorkforceAPI.getCustomProcesses();
      setCustomProcesses(res.data.processes || []);
    } catch (error) {
      console.error('Failed to fetch custom processes:', error);
    }
  };

  // All AI Tasks Functions
  const fetchAllAITasks = async () => {
    try {
      setAllAITasksLoading(true);
      const res = await aiWorkforceAPI.getAllAITasks();
      setAllAITasks(res.data.tasks || res.data || []);
    } catch (error) {
      console.error('Failed to fetch all AI tasks:', error);
      toast.error('Failed to load AI tasks');
    } finally {
      setAllAITasksLoading(false);
    }
  };

  const handleCreateCustomProcess = async () => {
    if (!newProcessForm.name) {
      toast.error('Process name is required');
      return;
    }
    if (newProcessForm.tasks.length === 0) {
      toast.error('Add at least one task to the process');
      return;
    }
    
    try {
      await aiWorkforceAPI.createCustomProcess({
        name: newProcessForm.name,
        description: newProcessForm.description,
        tasks: newProcessForm.tasks,
        schedule_type: newProcessForm.schedule_type
      });
      toast.success('Custom process created');
      setShowCreateProcessDialog(false);
      setNewProcessForm(DEFAULT_PROCESS_FORM);
      fetchCustomProcesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create process');
    }
  };

  // Note: handleAddTaskToProcess and handleRemoveTaskFromProcess are now handled inside CreateCustomProcessDialog

  const handleSaveQuickRun = async () => {
    if (!quickRunName.trim()) {
      toast.error('Please enter a name for this Quick Run');
      return;
    }
    try {
      await aiWorkforceAPI.createQuickRun({
        name: quickRunName,
        context: demoCustomInput,
        task_type: selectedDemoTask || 'custom_task'
      });
      toast.success('Quick Run saved!');
      setShowSaveQuickRunDialog(false);
      setQuickRunName('');
      fetchQuickRuns();
    } catch (error) {
      toast.error('Failed to save Quick Run');
    }
  };

  const handleQueueCustomProcess = async (processId) => {
    try {
      const res = await aiWorkforceAPI.queueCustomProcess(processId);
      toast.success(res.data.message);
      fetchOvernightQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue process');
    }
  };

  const handleDeleteCustomProcess = async (processId) => {
    if (!window.confirm('Are you sure you want to delete this process?')) return;
    
    try {
      await aiWorkforceAPI.deleteCustomProcess(processId);
      toast.success('Process deleted');
      fetchCustomProcesses();
    } catch (error) {
      toast.error('Failed to delete process');
    }
  };

  // Knowledge Base Handler
  const handleSaveKnowledge = async () => {
    try {
      if (editingKnowledge) {
        await aiWorkforceAPI.updateKnowledge(editingKnowledge.id, knowledgeForm);
        toast.success('Knowledge entry updated');
      } else {
        await aiWorkforceAPI.createKnowledge(knowledgeForm);
        toast.success('Knowledge entry added');
      }
      setShowKnowledgeDialog(false);
      setEditingKnowledge(null);
      setKnowledgeForm(DEFAULT_KNOWLEDGE_FORM);
      fetchKnowledge();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save knowledge entry');
    }
  };

  // Guideline Handler
  const handleSaveGuideline = async () => {
    try {
      if (editingGuideline) {
        await aiWorkforceAPI.updateGuideline(editingGuideline.id, guidelineForm);
        toast.success('Guideline updated');
      } else {
        await aiWorkforceAPI.createGuideline(guidelineForm);
        toast.success('Guideline added');
      }
      setShowGuidelineDialog(false);
      setEditingGuideline(null);
      setGuidelineForm(DEFAULT_GUIDELINE_FORM);
      fetchGuidelines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save guideline');
    }
  };

  // SOP Handler
  const handleSaveSOP = async () => {
    try {
      const sopData = editingSOP ? { ...editingSOP, ...sopForm } : sopForm;
      if (editingSOP) {
        await aiWorkforceAPI.updateSOP(editingSOP.id, sopData);
        toast.success('SOP updated');
      } else {
        await aiWorkforceAPI.createSOP(sopData);
        toast.success('SOP created');
      }
      setShowSOPDialog(false);
      setEditingSOP(null);
      setSOPForm(DEFAULT_SOP_FORM);
      fetchSOPs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SOP');
    }
  };

  // Create Custom Agent Handler
  const handleCreateCustomAgent = async () => {
    try {
      // Auto-generate mission from role and capabilities
      const missionParts = [`${newAgentForm.name} is a ${newAgentForm.role_title}`];
      if (newAgentForm.capabilities?.length > 0) {
        missionParts.push(`with expertise in ${newAgentForm.capabilities.join(', ')}`);
      }
      if (newAgentForm.description) {
        missionParts.push(`- ${newAgentForm.description}`);
      }
      const mission = missionParts.join(' ');
      
      const agentData = {
        name: newAgentForm.name,
        role_title: newAgentForm.role_title,
        description: newAgentForm.description,
        department: 'AI Workforce',
        status: 'active',
        capabilities: newAgentForm.capabilities,
        agent_type: newAgentForm.agent_type,
        mission: mission,
        ai_config: {
          left_brain_provider: 'openai',
          left_brain_model: 'gpt-4o',
          right_brain_provider: 'anthropic',
          right_brain_model: 'claude-sonnet-4-5-20250929',
          brain_stem_provider: 'gemini',
          brain_stem_model: 'gemini-2.0-flash',
          primary_model: newAgentForm.primary_model,
          temperature: 0.7
        }
      };
      
      await aiWorkforceAPI.createAgent(agentData);
      toast.success(`Agent "${newAgentForm.name}" created and activated`);
      setShowCreateAgentDialog(false);
      setNewAgentForm({ name: '', role_title: '', description: '', agent_type: 'text', primary_model: 'gpt-4o', capabilities: [], knowledge_base_ids: [], guideline_ids: [] });
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    }
  };

  // ==================== EDIT/DELETE HANDLERS ====================
  
  // Deliverable Edit/Delete
  const handleEditDeliverable = async () => {
    if (!selectedDeliverable) return;
    try {
      await aiWorkforceAPI.updateDeliverable(selectedDeliverable.id, deliverableEditForm);
      toast.success('Deliverable updated');
      setEditingDeliverable(false);
      fetchGalleryDeliverables();
      // Update the selected deliverable in state
      setSelectedDeliverable({...selectedDeliverable, ...deliverableEditForm});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update deliverable');
    }
  };

  const handleDeleteDeliverable = async (deliverableId) => {
    if (!window.confirm('Are you sure you want to delete this deliverable? This cannot be undone.')) return;
    try {
      await aiWorkforceAPI.deleteDeliverable(deliverableId);
      toast.success('Deliverable deleted');
      setShowDeliverableDialog(false);
      setSelectedDeliverable(null);
      fetchGalleryDeliverables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete deliverable');
    }
  };

  // Deliverable export handlers
  const handleExportToExcel = async () => {
    try {
      setExportLoading(true);
      const response = await aiWorkforceAPI.exportToExcel(selectedDeliverable.id);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai_export_${selectedDeliverable.id}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exported to Excel');
    } catch (error) {
      toast.error('Failed to export to Excel');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportToWord = async () => {
    try {
      setExportLoading(true);
      const response = await aiWorkforceAPI.exportToWord(selectedDeliverable.id);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai_document_${selectedDeliverable.id}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exported to Word');
    } catch (error) {
      toast.error('Failed to export to Word');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportToProspects = async () => {
    try {
      const preview = await aiWorkforceAPI.previewImport(selectedDeliverable.id);
      setImportPreview(preview.data);
      setFieldMappings(preview.data.suggested_mappings || {});
      setShowImportPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to preview import data');
    }
  };

  const handleLinkToCampaign = async () => {
    try {
      const res = await campaignsAPI.getAll({ limit: 100 });
      setCampaigns(res.data?.campaigns || res.data || []);
      setShowCampaignLinkDialog(true);
    } catch (error) {
      toast.error('Failed to load campaigns');
    }
  };

  const handleRegenerateDeliverable = () => {
    setRegenerateFeedback('');
    setShowRegenerateDialog(true);
  };

  const handleViewDeliverableHistory = async () => {
    try {
      const res = await aiWorkforceAPI.getDeliverableHistory(selectedDeliverable.id);
      setDeliverableHistory(res.data);
      setShowHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  // Overnight Queue Task Edit
  const handleEditOvernightTask = async () => {
    if (!editingOvernightTask) return;
    try {
      await aiWorkforceAPI.updateOvernightTask(editingOvernightTask.id, {
        title: overnightTaskForm.title,
        description: overnightTaskForm.description,
        priority: overnightTaskForm.priority,
        agent_type: overnightTaskForm.agent_type
      });
      toast.success('Task updated');
      setShowEditOvernightTaskDialog(false);
      setEditingOvernightTask(null);
      fetchOvernightQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Overnight Queue Log Delete
  const handleDeleteOvernightLog = async (logId) => {
    if (!window.confirm('Delete this log entry?')) return;
    try {
      await aiWorkforceAPI.deleteOvernightLog(logId);
      toast.success('Log deleted');
      fetchOvernightQueueLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete log');
    }
  };

  const handleClearAllOvernightLogs = async () => {
    if (!window.confirm('Clear ALL overnight queue logs? This cannot be undone.')) return;
    try {
      const res = await aiWorkforceAPI.clearAllOvernightLogs();
      toast.success(`Cleared ${res.data.deleted} log entries`);
      fetchOvernightQueueLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear logs');
    }
  };

  const handleUploadResource = async () => {
    if (!newResource.name || !newResource.file) {
      toast.error('Please provide a name and select a file');
      return;
    }
    
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const fileType = newResource.file.type.split('/')[1] || 'png';
        
        await aiWorkforceAPI.createResource({
          name: newResource.name,
          description: newResource.description,
          category: newResource.category,
          tags: newResource.tags.split(',').map(t => t.trim()).filter(t => t),
          base64_data: base64,
          file_type: fileType
        });
        
        toast.success('Resource uploaded successfully!');
        setShowUploadResourceDialog(false);
        setNewResource({ name: '', description: '', category: 'product', tags: '', file: null, preview: null });
        fetchResources();
      };
      reader.readAsDataURL(newResource.file);
    } catch (error) {
      toast.error('Failed to upload resource');
      console.error(error);
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await api.get('/ai-workforce/dashboard');
      setDashboardData(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFailedTasks = async () => {
    try {
      const res = await api.get('/ai-workforce/queue', { params: { status: 'failed' } });
      setFailedTasks(res.data || []);
    } catch (error) {
      console.error('Failed to fetch failed tasks:', error);
    }
  };

  const handleDashboardCardClick = (filter) => {
    setDashboardFilter(dashboardFilter === filter ? null : filter);
    if (filter === 'failed' && failedTasks.length === 0) {
      fetchFailedTasks();
    }
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const fetchAgents = async () => {
    try {
      const [agentsRes, templatesRes] = await Promise.all([
        api.get('/ai-workforce/agents'),
        api.get('/ai-workforce/agents/templates')
      ]);
      setAgents(agentsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    }
  };

  const fetchQueue = async () => {
    try {
      const params = {};
      if (queueFilter.status) params.status = queueFilter.status;
      if (queueFilter.agent_id) params.agent_id = queueFilter.agent_id;
      const res = await api.get('/ai-workforce/queue', { params });
      setQueueTasks(res.data);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    }
  };

  const fetchSOPs = async () => {
    try {
      const res = await api.get('/ai-workforce/sops');
      setSOPs(res.data);
    } catch (error) {
      console.error('Failed to fetch SOPs:', error);
    }
  };

  const fetchKnowledge = async () => {
    try {
      const res = await api.get('/ai-workforce/knowledge');
      setKnowledge(res.data);
    } catch (error) {
      console.error('Failed to fetch knowledge:', error);
    }
  };

  const fetchGuidelines = async () => {
    try {
      const res = await api.get('/ai-workforce/guidelines');
      setGuidelines(res.data);
    } catch (error) {
      console.error('Failed to fetch guidelines:', error);
    }
  };

  const fetchExecutionLogs = async () => {
    try {
      const res = await api.get('/ai-workforce/execution-logs', { params: { limit: 50 } });
      setExecutionLogs(res.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const fetchDemoTaskTypes = async () => {
    try {
      const res = await api.get('/ai-workforce/demo/task-types');
      setDemoTaskTypes(res.data);
      if (res.data.length > 0 && !selectedDemoTask) {
        setSelectedDemoTask(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch demo task types:', error);
    }
  };

  const runDemoTask = async () => {
    if (!selectedDemoTask) {
      toast.error('Please select a task type');
      return;
    }
    
    setDemoRunning(true);
    setDemoResult(null);
    setLeftBrainStream('');
    setRightBrainStream('');
    setBrainStemStream('');
    setStreamingPhase(null);
    setStreamingAgent(null);
    setDemoCompleted(false);  // Reset completion state
    
    if (streamingMode) {
      // Use streaming endpoint
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${backendUrl}/api/ai-workforce/demo/run-task-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            task_type: selectedDemoTask,
            custom_input: demoCustomInput || null,
            agent_id: selectedDemoAgent || null
          })
        });
        
        // Check if response is OK before attempting to read stream
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Server error: ${response.status} - ${errorText}`);
        }
        
        // Check if response body exists and is readable
        if (!response.body) {
          throw new Error('Response body is not available');
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        // Use local variables to accumulate stream content
        let leftContent = '';
        let rightContent = '';
        let cortexContent = '';
        let currentAgent = null;
        
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            const text = decoder.decode(value);
            const lines = text.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  switch (data.type) {
                    case 'agent_assigned':
                      currentAgent = data.agent;
                      setStreamingAgent(data.agent);
                      break;
                    case 'phase_started':
                      setStreamingPhase(data.phase);
                      if (data.phase === 'task_creation') {
                        setCreatedTasks([]);
                      } else if (data.phase === 'task_execution') {
                        setExecutingTasks(true);
                        setDeliverables([]);
                      }
                      break;
                    case 'left_brain_chunk':
                      leftContent += data.content;
                      setLeftBrainStream(leftContent);
                      break;
                    case 'right_brain_chunk':
                      rightContent += data.content;
                      setRightBrainStream(rightContent);
                      break;
                    case 'brain_stem_chunk':
                    case 'cortex_chunk':
                      cortexContent += data.content;
                      setBrainStemStream(cortexContent);
                      break;
                    case 'phase_completed':
                      if (data.phase === 'cortex' || data.phase === 'brain_stem') {
                        setStreamingPhase('completed');
                      } else if (data.phase === 'task_execution') {
                        setExecutingTasks(false);
                      }
                      break;
                    case 'tasks_created':
                      setCreatedTasks(data.tasks || []);
                      toast.info(`Created ${data.count} tasks from execution pathway`);
                      break;
                    case 'task_executing':
                      setCreatedTasks(prev => prev.map(t => 
                        t.id === data.task_id ? {...t, status: 'executing'} : t
                      ));
                      break;
                    case 'task_completed':
                      setCreatedTasks(prev => prev.map(t => 
                        t.id === data.task_id ? {...t, status: data.success ? 'completed' : 'failed'} : t
                      ));
                      break;
                    case 'deliverable_created':
                      setDeliverables(prev => [...prev, data.deliverable]);
                      break;
                    case 'task_error':
                      toast.error(`Task failed: ${data.error}`);
                      break;
                    case 'pathway_error':
                      console.warn('Pathway processing error:', data.error);
                      break;
                    case 'completed':
                      setDemoResult({
                        success: true,
                        agent: currentAgent,
                        execution_result: {
                          left_brain_analysis: leftContent,
                          right_brain_analysis: rightContent,
                          output: cortexContent
                        }
                      });
                      setStreamingPhase('completed');
                      setDemoCompleted(true);  // Mark demo as completed - keeps sections expanded
                      setExecutingTasks(false);
                      toast.success(data.message || 'Execution completed successfully');
                      fetchDashboard();
                      fetchExecutionLogs();
                      fetchQueue();  // Refresh queue to show new tasks
                      break;
                    case 'error':
                      toast.error(data.message);
                      setDemoResult({ success: false, error: data.message });
                      break;
                    default:
                      break;
                  }
                } catch (e) {
                  // Ignore parse errors for incomplete chunks
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      } catch (error) {
        toast.error('Streaming failed: ' + error.message);
        setDemoResult({ success: false, error: error.message });
      } finally {
        setDemoRunning(false);
        // Don't reset streamingPhase to null if completed - keep sections expanded
        // streamingPhase stays as 'completed' if the demo finished successfully
      }
    } else {
      // Use regular endpoint
      try {
        const res = await api.post('/ai-workforce/demo/run-task', {
          task_type: selectedDemoTask,
          custom_input: demoCustomInput || null
        });
        
        setDemoResult(res.data);
        
        if (res.data.success) {
          toast.success(`Demo completed by ${res.data.agent?.name || 'AI Agent'}`);
          fetchDashboard();
          fetchExecutionLogs();
        } else {
          toast.error(res.data.message || 'Demo task failed');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to run demo task');
        setDemoResult({
          success: false,
          error: error.response?.data?.detail || 'Failed to run demo task'
        });
      } finally {
        setDemoRunning(false);
      }
    }
  };

  const handleCreateFromTemplate = async (templateIndex) => {
    try {
      await api.post('/ai-workforce/agents/from-template', null, {
        params: { template_index: templateIndex }
      });
      toast.success('Agent created from template');
      setShowTemplateDialog(false);
      fetchAgents();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    }
  };

  const handleActivateAgent = async (agentId) => {
    try {
      await api.put(`/ai-workforce/agents/${agentId}/activate`);
      toast.success('Agent activated');
      fetchAgents();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to activate agent');
    }
  };

  const handleSuspendAgent = async (agentId) => {
    try {
      await api.put(`/ai-workforce/agents/${agentId}/suspend`);
      toast.success('Agent suspended');
      fetchAgents();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to suspend agent');
    }
  };

  const handleApproveTask = async (taskId, approved) => {
    try {
      await api.put(`/ai-workforce/queue/${taskId}/approve`, null, {
        params: { approved }
      });
      toast.success(approved ? 'Task approved' : 'Task rejected');
      fetchQueue();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleEditAgent = (agent) => {
    setEditingAgent({
      ...agent,
      ai_config: {
        left_brain_model: agent.ai_config?.left_brain_model || 'gpt-4o',
        left_brain_provider: agent.ai_config?.left_brain_provider || 'openai',
        right_brain_model: agent.ai_config?.right_brain_model || 'claude-sonnet-4-5-20250929',
        right_brain_provider: agent.ai_config?.right_brain_provider || 'anthropic',
        brain_stem_model: agent.ai_config?.brain_stem_model || 'gemini-2.5-flash',
        brain_stem_provider: agent.ai_config?.brain_stem_provider || 'gemini',
        temperature: agent.ai_config?.temperature || 0.7,
        max_tokens: agent.ai_config?.max_tokens || 2000
      }
    });
    setSelectedAgent(null);
  };

  const handleSaveAgent = async () => {
    if (!editingAgent) return;
    
    try {
      setSavingAgent(true);
      await api.put(`/ai-workforce/agents/${editingAgent.id}`, {
        name: editingAgent.name,
        role_title: editingAgent.role_title,
        mission: editingAgent.mission,
        primary_queues: editingAgent.primary_queues,
        automates_well: editingAgent.automates_well,
        ai_config: editingAgent.ai_config,
        kpis: editingAgent.kpis
      });
      toast.success('Agent updated successfully');
      setEditingAgent(null);
      fetchAgents();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    } finally {
      setSavingAgent(false);
    }
  };

  // AI Model options for each brain component
  const modelOptions = {
    openai: [
      { value: 'gpt-4o', label: 'GPT-4o (Fast & Capable)' },
      { value: 'gpt-5.2', label: 'GPT-5.2 (Latest)' },
      { value: 'gpt-4.1', label: 'GPT-4.1' },
      { value: 'o3', label: 'O3 (Reasoning)' }
    ],
    anthropic: [
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
      { value: 'claude-4-sonnet-20250514', label: 'Claude 4 Sonnet' },
      { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5' },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' }
    ],
    gemini: [
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (Fast)' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash' },
      { value: 'gemini-3-pro-preview', label: 'Gemini 3 Pro' }
    ]
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-workforce-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Bot className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">AI Workforce</h1>
            <p className="text-muted-foreground">Virtual employees powered by multi-AI architecture</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchDashboard(); fetchAgents(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <TemplateDialog
            open={showTemplateDialog}
            onOpenChange={setShowTemplateDialog}
            templates={templates}
            onSelectTemplate={handleCreateFromTemplate}
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1 w-full justify-start bg-muted/50 p-1 rounded-lg">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="flex-shrink-0">
            <BarChart3 className="h-4 w-4 mr-1" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="agents" data-testid="tab-agents" className="flex-shrink-0">
            <Users className="h-4 w-4 mr-1" />
            Agents
          </TabsTrigger>
          <TabsTrigger value="queue" data-testid="tab-queue" className="flex-shrink-0">
            <List className="h-4 w-4 mr-1" />
            Work Queue
          </TabsTrigger>
          <TabsTrigger value="sops" data-testid="tab-sops" className="flex-shrink-0">
            <FileText className="h-4 w-4 mr-1" />
            SOPs
          </TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="tab-knowledge" className="flex-shrink-0">
            <Brain className="h-4 w-4 mr-1" />
            Knowledge
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs" className="flex-shrink-0">
            <Activity className="h-4 w-4 mr-1" />
            Logs
          </TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects" className="flex-shrink-0 bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 data-[state=active]:from-emerald-200 data-[state=active]:to-green-200">
            <FolderOpen className="h-4 w-4 mr-1" />
            Projects
          </TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery" className="flex-shrink-0 bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-800 data-[state=active]:from-teal-200 data-[state=active]:to-cyan-200">
            <ImageIcon className="h-4 w-4 mr-1" />
            Gallery
          </TabsTrigger>
          <TabsTrigger value="resources" data-testid="tab-resources" className="flex-shrink-0 bg-gradient-to-r from-indigo-100 to-violet-100 text-indigo-800 data-[state=active]:from-indigo-200 data-[state=active]:to-violet-200">
            <Upload className="h-4 w-4 mr-1" />
            Resources
          </TabsTrigger>
          <TabsTrigger value="demo" data-testid="tab-demo" className="flex-shrink-0 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 data-[state=active]:from-amber-200 data-[state=active]:to-orange-200">
            <Sparkles className="h-4 w-4 mr-1" />
            Demo Mode
          </TabsTrigger>
          <TabsTrigger value="overnight" data-testid="tab-overnight" className="flex-shrink-0 bg-gradient-to-r from-slate-100 to-gray-100 text-slate-800 data-[state=active]:from-slate-200 data-[state=active]:to-gray-200">
            <Clock className="h-4 w-4 mr-1" />
            Overnight Queue
          </TabsTrigger>
          <TabsTrigger value="all-ai-tasks" data-testid="tab-all-ai-tasks" className="flex-shrink-0 bg-gradient-to-r from-purple-100 to-violet-100 text-purple-800 data-[state=active]:from-purple-200 data-[state=active]:to-violet-200">
            <Brain className="h-4 w-4 mr-1" />
            All AI Tasks
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab (extracted component) */}
        <DashboardTab
          dashboardData={dashboardData}
          dashboardFilter={dashboardFilter}
          onDashboardFilterChange={(filter) => setDashboardFilter(dashboardFilter === filter ? null : filter)}
          agents={agents}
          failedTasks={failedTasks}
          collapsedSections={collapsedSections}
          onToggleSection={toggleSection}
          onNavigateToQueue={() => setActiveTab('queue')}
        />

        {/* Agents Tab (extracted component) */}
        <AgentsTab
          agents={agents}
          onShowTemplateDialog={() => setShowTemplateDialog(true)}
          onShowCreateAgentDialog={() => setShowCreateAgentDialog(true)}
          onActivateAgent={handleActivateAgent}
          onSuspendAgent={handleSuspendAgent}
          onSelectAgent={setSelectedAgent}
        />

        {/* Work Queue Tab (extracted component) */}
        <QueueTab
          queueTasks={queueTasks}
          queueFilter={queueFilter}
          onQueueFilterChange={setQueueFilter}
          agents={agents}
          onRefresh={fetchQueue}
          onTaskClick={(task) => {
            setSelectedQueueTask(task);
            setShowQueueDetailDialog(true);
          }}
          onApproveTask={handleApproveTask}
        />

        {/* SOPs Tab (extracted component) */}
        <SOPsTab
          sops={sops}
          onAddSOP={() => setShowSOPDialog(true)}
          onEditSOP={(sop) => { setEditingSOP(sop); setShowSOPDialog(true); }}
        />

        {/* Knowledge Tab (extracted component) */}
        <KnowledgeTab
          knowledge={knowledge}
          guidelines={guidelines}
          onAddKnowledge={() => setShowKnowledgeDialog(true)}
          onEditKnowledge={(k) => { setEditingKnowledge(k); setShowKnowledgeDialog(true); }}
          onAddGuideline={() => setShowGuidelineDialog(true)}
          onEditGuideline={(g) => { setEditingGuideline(g); setShowGuidelineDialog(true); }}
        />

        {/* Execution Logs Tab (extracted component) */}
        <LogsTab
          executionLogs={executionLogs}
          onLogClick={(log) => { setSelectedLog(log); setShowLogDetailDialog(true); }}
        />

        {/* AI Projects Tab (extracted component) */}
        <ProjectsTab
          projects={projects}
          onRefresh={fetchProjects}
          onProjectClick={async (project) => {
            setSelectedProject(project);
            await fetchProjectKanban(project.id);
            setShowProjectDialog(true);
          }}
          onNavigateToDemo={() => setActiveTab('demo')}
        />

        {/* Deliverables Gallery Tab (extracted component) */}
        <GalleryTab
          galleryDeliverables={galleryDeliverables}
          galleryProjects={galleryProjects}
          galleryStats={galleryStats}
          galleryViewMode={galleryViewMode}
          onViewModeChange={setGalleryViewMode}
          galleryFilter={galleryFilter}
          onFilterChange={(type) => {
            setGalleryFilter(type);
            setTimeout(fetchGalleryDeliverables, 100);
          }}
          gallerySearch={gallerySearch}
          onSearchChange={setGallerySearch}
          onSearch={fetchGalleryDeliverables}
          onRefresh={fetchGalleryDeliverables}
          expandedProjects={expandedProjects}
          onToggleProject={(projectId) => setExpandedProjects(prev => ({...prev, [projectId]: !prev[projectId]}))}
          onDeliverableClick={(deliverable) => {
            setSelectedDeliverable(deliverable);
            setShowDeliverableDialog(true);
          }}
          onNavigateToDemo={() => setActiveTab('demo')}
        />

        {/* Resource Library Tab (extracted component) */}
        <ResourcesTab
          resources={resources}
          linkedAssets={linkedAssets}
          resourceStats={resourceStats}
          resourceViewMode={resourceViewMode}
          onViewModeChange={(value) => {
            setResourceViewMode(value);
            setTimeout(fetchResources, 100);
          }}
          resourceFilter={resourceFilter}
          onFilterChange={(cat) => {
            setResourceFilter(cat);
            setTimeout(fetchResources, 100);
          }}
          onRefresh={fetchResources}
          onUploadResource={() => setShowUploadResourceDialog(true)}
          onImportFromAssets={async () => {
            try {
              const res = await aiWorkforceAPI.getLinkedAssets({});
              setLinkedAssets(res.data.assets || []);
            } catch (error) {
              console.error('Failed to fetch assets:', error);
            }
            setShowImportFromAssetsDialog(true);
          }}
          onResourceClick={(resource) => {
            setSelectedResource(resource);
            setShowResourceDialog(true);
          }}
        />


        {/* Demo Mode Tab (extracted component) */}
        <DemoModeTab
          selectedDemoTask={selectedDemoTask}
          onSelectDemoTask={(taskId) => { setSelectedDemoTask(taskId); setDemoResult(null); setExpandedBrain(null); }}
          selectedDemoAgent={selectedDemoAgent}
          onSelectDemoAgent={setSelectedDemoAgent}
          agents={agents}
          selectedCampaignId={selectedCampaignId}
          onSelectCampaign={setSelectedCampaignId}
          campaigns={campaigns}
          onLoadCampaigns={async () => {
            try {
              const res = await campaignsAPI.getAll({ limit: 100 });
              setCampaigns(res.data || []);
            } catch (error) {
              toast.error('Failed to load campaigns');
            }
          }}
          demoCustomInput={demoCustomInput}
          onCustomInputChange={setDemoCustomInput}
          streamingMode={streamingMode}
          onStreamingModeChange={setStreamingMode}
          demoRunning={demoRunning}
          streamingPhase={streamingPhase}
          streamingAgent={streamingAgent}
          leftBrainStream={leftBrainStream}
          rightBrainStream={rightBrainStream}
          brainStemStream={brainStemStream}
          expandedBrain={expandedBrain}
          onExpandedBrainChange={setExpandedBrain}
          demoCompleted={demoCompleted}
          demoResult={demoResult}
          expandedResultBrain={expandedResultBrain}
          onExpandedResultBrainChange={setExpandedResultBrain}
          createdTasks={createdTasks}
          executingTasks={executingTasks}
          deliverables={deliverables}
          quickRuns={quickRuns}
          onShowSaveQuickRunDialog={() => setShowSaveQuickRunDialog(true)}
          onLoadQuickRun={(qr) => {
            setDemoCustomInput(qr.context);
            setSelectedDemoTask(qr.task_type);
            toast.info(`Loaded "${qr.name}"`);
          }}
          onRunDemo={runDemoTask}
          onCopyResults={() => {
            navigator.clipboard.writeText(JSON.stringify(demoResult.execution_result, null, 2));
            toast.success('Results copied to clipboard');
          }}
          toast={toast}
        />

        {/* Overnight Queue Tab (extracted component) */}
        <OvernightQueueTab
          overnightQueue={overnightQueue}
          overnightQueueLogs={overnightQueueLogs}
          customProcesses={customProcesses}
          overnightQueueRunning={overnightQueueRunning}
          overnightTaskForm={overnightTaskForm}
          setOvernightTaskForm={setOvernightTaskForm}
          setEditingOvernightTask={setEditingOvernightTask}
          onShowAddTaskDialog={() => setShowAddOvernightTaskDialog(true)}
          onShowEditTaskDialog={() => setShowEditOvernightTaskDialog(true)}
          onShowCreateProcessDialog={() => setShowCreateProcessDialog(true)}
          onClearCompleted={handleClearCompletedOvernightTasks}
          onRunNow={handleRunOvernightQueueNow}
          onRemoveTask={handleRemoveOvernightTask}
          onClearAllLogs={handleClearAllOvernightLogs}
          onDeleteLog={handleDeleteOvernightLog}
          onQueueProcess={handleQueueCustomProcess}
          onDeleteProcess={handleDeleteCustomProcess}
        />

        {/* All AI Tasks Tab (extracted component) */}
        <AllAITasksTab
          allTasks={allAITasks}
          onRefresh={fetchAllAITasks}
          isLoading={allAITasksLoading}
        />
      </Tabs>

      {/* Agent Detail Dialog (extracted component) */}
      <AgentDetailDialog
        agent={selectedAgent}
        onClose={() => setSelectedAgent(null)}
        onEdit={handleEditAgent}
      />

      {/* Edit Agent Dialog (extracted component) */}
      <EditAgentDialog
        agent={editingAgent}
        onAgentChange={setEditingAgent}
        onSave={handleSaveAgent}
        onCancel={() => setEditingAgent(null)}
        saving={savingAgent}
      />

      {/* Queue Task Detail Dialog (extracted component) */}
      <QueueTaskDetailDialog
        open={showQueueDetailDialog}
        onOpenChange={setShowQueueDetailDialog}
        task={selectedQueueTask}
        onApprove={handleApproveTask}
        onClose={() => setShowQueueDetailDialog(false)}
      />

      {/* Knowledge Base Dialog (extracted component) */}
      <KnowledgeDialog
        open={showKnowledgeDialog}
        onOpenChange={setShowKnowledgeDialog}
        formData={knowledgeForm}
        onFormChange={setKnowledgeForm}
        editingKnowledge={editingKnowledge}
        agents={agents}
        onSave={handleSaveKnowledge}
        onCancel={() => { setShowKnowledgeDialog(false); setEditingKnowledge(null); }}
      />

      {/* Guidelines Dialog (extracted component) */}
      <GuidelineDialog
        open={showGuidelineDialog}
        onOpenChange={setShowGuidelineDialog}
        formData={guidelineForm}
        onFormChange={setGuidelineForm}
        editingGuideline={editingGuideline}
        agents={agents}
        onSave={handleSaveGuideline}
        onCancel={() => { setShowGuidelineDialog(false); setEditingGuideline(null); }}
      />

      {/* Create Custom Agent Dialog (extracted component) */}
      <CreateCustomAgentDialog
        open={showCreateAgentDialog}
        onOpenChange={setShowCreateAgentDialog}
        formData={newAgentForm}
        onFormChange={setNewAgentForm}
        onSubmit={handleCreateCustomAgent}
        onCancel={() => setShowCreateAgentDialog(false)}
      />

      {/* SOP Dialog (extracted component) */}
      <SOPDialog
        open={showSOPDialog}
        onOpenChange={(open) => { setShowSOPDialog(open); if (!open) setEditingSOP(null); }}
        formData={sopForm}
        onFormChange={setSOPForm}
        editingSOP={editingSOP}
        onEditingSOPChange={setEditingSOP}
        agents={agents}
        onSave={handleSaveSOP}
        onCancel={() => { setShowSOPDialog(false); setEditingSOP(null); }}
      />

      {/* Execution Log Detail Dialog (extracted component) */}
      <LogDetailDialog
        open={showLogDetailDialog}
        onOpenChange={setShowLogDetailDialog}
        log={selectedLog}
        onClose={() => setShowLogDetailDialog(false)}
      />
      
      {/* Save Quick Run Dialog (extracted component) */}
      <SaveQuickRunDialog
        open={showSaveQuickRunDialog}
        onOpenChange={setShowSaveQuickRunDialog}
        quickRunName={quickRunName}
        onNameChange={setQuickRunName}
        contextPreview={demoCustomInput}
        taskType={selectedDemoTask}
        onSave={handleSaveQuickRun}
        onCancel={() => setShowSaveQuickRunDialog(false)}
      />
      
      {/* Deliverable Detail Dialog (extracted component) */}
      <DeliverableDetailDialog
        open={showDeliverableDialog}
        onOpenChange={setShowDeliverableDialog}
        deliverable={selectedDeliverable}
        editing={editingDeliverable}
        onEditingChange={setEditingDeliverable}
        editForm={deliverableEditForm}
        onEditFormChange={setDeliverableEditForm}
        onSaveEdit={handleEditDeliverable}
        onDelete={handleDeleteDeliverable}
        onExportExcel={handleExportToExcel}
        onExportWord={handleExportToWord}
        onImportToProspects={handleImportToProspects}
        onLinkToCampaign={handleLinkToCampaign}
        onRegenerate={handleRegenerateDeliverable}
        onViewHistory={handleViewDeliverableHistory}
        exportLoading={exportLoading}
      />
      
      {/* Campaign Link Dialog (extracted component) */}
      <CampaignLinkDialog
        open={showCampaignLinkDialog}
        onOpenChange={setShowCampaignLinkDialog}
        campaigns={campaigns}
        selectedCampaignId={selectedCampaignId}
        onCampaignChange={setSelectedCampaignId}
        onLink={async () => {
          try {
            await aiWorkforceAPI.linkDeliverableToCampaign(selectedDeliverable.id, selectedCampaignId);
            toast.success('Deliverable linked to campaign');
            setShowCampaignLinkDialog(false);
            setSelectedCampaignId('');
          } catch (error) {
            toast.error('Failed to link deliverable');
          }
        }}
        onCancel={() => setShowCampaignLinkDialog(false)}
      />
      
      {/* Import Preview Dialog (extracted component) */}
      <ImportPreviewDialog
        open={showImportPreviewDialog}
        onOpenChange={setShowImportPreviewDialog}
        importPreview={importPreview}
        fieldMappings={fieldMappings}
        onFieldMappingChange={setFieldMappings}
        selectedCampaignId={selectedCampaignId}
        onCampaignChange={setSelectedCampaignId}
        campaigns={campaigns}
        onLoadCampaigns={async () => {
          try {
            const res = await campaignsAPI.getAll({ limit: 100 });
            setCampaigns(res.data || []);
          } catch (error) {
            toast.error('Failed to load campaigns');
          }
        }}
        importLoading={importLoading}
        onImport={async () => {
          try {
            setImportLoading(true);
            const mappingForBackend = {};
            Object.entries(fieldMappings).forEach(([header, field]) => {
              if (field !== 'skip') {
                mappingForBackend[header.toLowerCase().replace(' ', '_')] = field;
              }
            });
            
            const result = await aiWorkforceAPI.importToProspects(
              selectedDeliverable.id, 
              mappingForBackend,
              selectedCampaignId || null
            );
            toast.success(`Successfully imported ${result.data.imported_count} prospects${selectedCampaignId ? ' to campaign' : ''}`);
            setShowImportPreviewDialog(false);
            setShowDeliverableDialog(false);
            setSelectedCampaignId('');
          } catch (error) {
            toast.error('Failed to import prospects');
          } finally {
            setImportLoading(false);
          }
        }}
        onCancel={() => setShowImportPreviewDialog(false)}
      />
      
      {/* Regenerate / Create Variations Dialog (extracted component) */}
      <RegenerateDialog
        open={showRegenerateDialog}
        onOpenChange={setShowRegenerateDialog}
        deliverable={selectedDeliverable}
        feedback={regenerateFeedback}
        onFeedbackChange={setRegenerateFeedback}
        regenerating={regenerating}
        onRegenerate={async () => {
          try {
            setRegenerating(true);
            const res = await aiWorkforceAPI.regenerateDeliverable(
              selectedDeliverable.id, 
              regenerateFeedback || null,
              3
            );
            setVariations(res.data.variations || []);
            setShowRegenerateDialog(false);
            setShowVariationsDialog(true);
            toast.success(`Generated ${res.data.variations_generated} variations`);
          } catch (error) {
            toast.error('Failed to generate variations');
          } finally {
            setRegenerating(false);
          }
        }}
        onCancel={() => setShowRegenerateDialog(false)}
      />
      
      {/* Variations Selection Dialog (extracted component) */}
      <VariationsDialog
        open={showVariationsDialog}
        onOpenChange={setShowVariationsDialog}
        variations={variations}
        selectedIndex={selectedVariationIndex}
        onSelectVariation={setSelectedVariationIndex}
        onConfirm={async () => {
          try {
            await aiWorkforceAPI.selectVariation(
              selectedDeliverable.id,
              selectedVariationIndex,
              null
            );
            toast.success('Variation selected successfully');
            setShowVariationsDialog(false);
            setShowDeliverableDialog(false);
            setSelectedVariationIndex(null);
            fetchGalleryDeliverables();
          } catch (error) {
            toast.error('Failed to select variation');
          }
        }}
        onCancel={() => {
          setShowVariationsDialog(false);
          setSelectedVariationIndex(null);
        }}
      />
      
      {/* Revision History Dialog (extracted component) */}
      <HistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
        deliverableHistory={deliverableHistory}
        onRevert={async (version) => {
          try {
            await aiWorkforceAPI.revertDeliverable(selectedDeliverable.id, version);
            toast.success(`Reverted to version ${version}`);
            setShowHistoryDialog(false);
            setShowDeliverableDialog(false);
            fetchGalleryDeliverables();
          } catch (error) {
            toast.error('Failed to revert');
          }
        }}
        onClose={() => setShowHistoryDialog(false)}
      />
      
      {/* Project Details Dialog (extracted component) */}
      <ProjectDialog
        open={showProjectDialog}
        onOpenChange={setShowProjectDialog}
        project={selectedProject}
        projectKanban={projectKanban}
        viewMode={projectViewMode}
        onViewModeChange={setProjectViewMode}
        onTaskClick={async (task) => {
          try {
            setLoadingTaskDetail(true);
            const res = await aiWorkforceAPI.getTaskDetail(task.id);
            setTaskDetail(res.data);
            setSelectedKanbanTask(task);
            setShowTaskDetailDialog(true);
          } catch (error) {
            toast.error('Failed to load task details');
          } finally {
            setLoadingTaskDetail(false);
          }
        }}
        onViewDeliverable={async (task) => {
          try {
            const res = await aiWorkforceAPI.getTaskDetail(task.id);
            if (res.data?.deliverables?.length > 0) {
              setSelectedDeliverable({
                ...res.data.deliverables[0],
                id: `${task.id}_0`,
                task_id: task.id,
                agent_name: task.agent_name
              });
              setShowDeliverableDialog(true);
            }
          } catch (error) {
            toast.error('Failed to load deliverable');
          }
        }}
        onClose={() => setShowProjectDialog(false)}
        onViewDeliverables={() => {
          setShowProjectDialog(false);
          setActiveTab('gallery');
        }}
      />
      
      {/* Task Detail Dialog (extracted component) */}
      <TaskDetailDialog
        open={showTaskDetailDialog}
        onOpenChange={setShowTaskDetailDialog}
        taskDetail={taskDetail}
        loading={loadingTaskDetail}
        onViewDeliverable={(deliverable) => {
          setSelectedDeliverable(deliverable);
          setShowDeliverableDialog(true);
        }}
        onClose={() => setShowTaskDetailDialog(false)}
      />
      
      {/* Upload Resource Dialog (extracted component) */}
      <UploadResourceDialog
        open={showUploadResourceDialog}
        onOpenChange={setShowUploadResourceDialog}
        formData={newResource}
        onFormChange={setNewResource}
        onUpload={handleUploadResource}
        onCancel={() => setShowUploadResourceDialog(false)}
      />
      
      {/* View Resource Dialog (extracted component) */}
      <ViewResourceDialog
        open={showResourceDialog}
        onOpenChange={setShowResourceDialog}
        resource={selectedResource}
        onSyncToAssetLibrary={handleSyncToAssetLibrary}
        onImportFromAssetLibrary={handleImportFromAssetLibrary}
        onDelete={handleDeleteResource}
        onClose={() => setShowResourceDialog(false)}
      />

      {/* Add Overnight Task Dialog (extracted component) */}
      <AddOvernightTaskDialog
        open={showAddOvernightTaskDialog}
        onOpenChange={setShowAddOvernightTaskDialog}
        formData={overnightTaskForm}
        onFormChange={setOvernightTaskForm}
        onSubmit={handleAddOvernightTask}
        onCancel={() => setShowAddOvernightTaskDialog(false)}
      />

      {/* Edit Overnight Task Dialog (extracted component) */}
      <EditOvernightTaskDialog
        open={showEditOvernightTaskDialog}
        onOpenChange={setShowEditOvernightTaskDialog}
        formData={overnightTaskForm}
        onFormChange={setOvernightTaskForm}
        onSave={handleEditOvernightTask}
        onCancel={() => {
          setShowEditOvernightTaskDialog(false);
          setEditingOvernightTask(null);
        }}
      />

      {/* Create Custom Process Dialog (extracted component) */}
      <CreateCustomProcessDialog
        open={showCreateProcessDialog}
        onOpenChange={setShowCreateProcessDialog}
        formData={newProcessForm}
        onFormChange={setNewProcessForm}
        onSubmit={handleCreateCustomProcess}
        onCancel={() => setShowCreateProcessDialog(false)}
      />

      {/* Import from Asset Library Dialog (extracted component) */}
      <ImportFromAssetsDialog
        open={showImportFromAssetsDialog}
        onOpenChange={setShowImportFromAssetsDialog}
        linkedAssets={linkedAssets}
        onImportAsset={handleImportFromAssetLibrary}
        onCancel={() => setShowImportFromAssetsDialog(false)}
        onOpenAssetRepository={() => window.location.href = '/assets'}
      />
    </div>
  );
}
